
import { useState, type FormEvent } from 'react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardBody,
  CardHead,
  ConfirmDialog,
  CopyToast,
  EmptyState,
  Input,
  Pill,
  Select,
  Table,
  TableActions,
  tableCell,
  useCopyToast,
} from '../../components/ui';
import { useCurrentUser } from '../../hooks';
import {
  useAdminUsers,
  useCreateAdminUser,
  useResetAdminUserPassword,
  useUpdateAdminUser,
} from '../../hooks/useAdminUsers';
import type { AdminUser, AdminUserCredentials, UserRole } from '../../types/api';
import styles from './AdminUsersPage.module.css';

const ROLE_OPTIONS: readonly { value: UserRole; label: string }[] = [
  { value: 'viewer', label: 'viewer, только смотрит' },
  { value: 'editor', label: 'editor, работает с задачами' },
  { value: 'admin', label: 'admin, плюс управление людьми' },
];

type Pending = { kind: 'reset'; user: AdminUser } | null;

export function AdminUsersPage() {
  const { user: me, isLoading: meLoading } = useCurrentUser();
  const isAdmin = me?.role === 'admin';

  const listQ = useAdminUsers(isAdmin);
  const createM = useCreateAdminUser();
  const resetM = useResetAdminUserPassword();
  const patchM = useUpdateAdminUser();
  const toast = useCopyToast();

  const [newUsername, setNewUsername] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('viewer');
  const [credentials, setCredentials] = useState<AdminUserCredentials | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  const actionError = createM.error ?? resetM.error ?? patchM.error;

  if (meLoading) {
    return <div className={styles.hint}>Проверяем доступ…</div>;
  }

  if (!isAdmin) {
    return (
      <div className={styles.noAccess}>
        <h2>Нет доступа</h2>
        <p>Эта страница доступна только администраторам.</p>
      </div>
    );
  }

  const submitCreate = (event: FormEvent) => {
    event.preventDefault();
    const username = newUsername.trim().toLowerCase();
    if (!username) return;
    createM.mutate(
      { username, role: newRole },
      {
        onSuccess: (created) => {
          setCredentials(created);
          setNewUsername('');
          setNewRole('viewer');
        },
      },
    );
  };

  const confirmPending = () => {
    if (!pending) return;
    resetM.mutate(pending.user.id, {
      onSuccess: (issued) => {
        setCredentials(issued);
        setPending(null);
      },
    });
  };

  const users = listQ.data ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <h1 className={styles.title}>Пользователи</h1>
        <span className={styles.subtitle}>
          Регистрации нет: аккаунт заводит админ, пароль придумывает сервер.
        </span>
      </header>

      {actionError && <ErrorBanner error={actionError} />}

      <Card>
        <CardHead title="Создать нового" />
        <CardBody pad>
          <form className={styles.createForm} onSubmit={submitCreate}>
            <Input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="ник, например monter1"
              autoComplete="off"
              required
              className={styles.emailInput}
              aria-label="Логин нового пользователя"
            />
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as UserRole)}
              className={styles.roleSelect}
              aria-label="Роль"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Button type="submit" variant="primary" disabled={createM.isPending}>
              {createM.isPending ? 'Создаём…' : 'Создать'}
            </Button>
          </form>

          {credentials && (
            <div className={styles.credsBox}>
              <div className={styles.credsTitle}>
                Готово. Скопируйте и передайте: второй раз пароль не покажем.
              </div>
              <div className={styles.credsRow}>
                <span className={styles.credsLabel}>Логин</span>
                <code className={styles.credsValue}>{credentials.user.username}</code>
              </div>
              <div className={styles.credsRow}>
                <span className={styles.credsLabel}>Пароль</span>
                <code className={`${styles.credsValue} ${styles.credsPassword}`}>
                  {credentials.password}
                </code>
                <Button
                  size="xs"
                  onClick={() =>
                    toast.copy(
                      `${credentials.user.username} / ${credentials.password}`,
                      'Логин и пароль скопированы',
                    )
                  }
                >
                  Копировать
                </Button>
              </div>
              <Button
                size="xs"
                variant="ghost"
                className={styles.dismiss}
                onClick={() => setCredentials(null)}
              >
                скрыть
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Список" hint={<Pill variant="solid">{users.length}</Pill>} />
        <CardBody>
          {listQ.isPending && <div className={styles.hint}>Загружаем…</div>}
          {listQ.error && <ErrorBanner error={listQ.error} onRetry={() => void listQ.refetch()} />}

          {listQ.data && users.length === 0 && (
            <EmptyState
              title="Пользователей нет"
              description="Заведите первого в форме выше, пароль сгенерируется автоматически."
            />
          )}

          {users.length > 0 && (
            <Table bare>
              <thead>
                <tr>
                  <th>Логин</th>
                  <th>Команда</th>
                  <th>Должность</th>
                  <th>Роль</th>
                  <th>Статус</th>
                  <th>Создан</th>
                  <th>Последний вход</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isMe = user.id === me?.id;
                  return (
                    <tr key={user.id} className={user.is_active ? undefined : styles.disabledRow}>
                      <td className={tableCell.mono}>
                        {user.username}
                        {isMe && <span className={styles.youBadge}>вы</span>}
                      </td>
                      <td>{user.team ?? '—'}</td>
                      <td>{user.job ?? '—'}</td>
                      <td>
                        {isMe || user.crm_matched ? (
                          <Pill
                            tone="info"
                            title={user.crm_matched ? 'Роль задаётся в CRM' : undefined}
                          >
                            {user.role}
                          </Pill>
                        ) : (
                          <Select
                            size="sm"
                            value={user.role}
                            disabled={patchM.isPending}
                            aria-label={`Роль ${user.username}`}
                            onChange={(e) =>
                              patchM.mutate({
                                userId: user.id,
                                body: { role: e.target.value as UserRole },
                              })
                            }
                          >
                            {ROLE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.value}
                              </option>
                            ))}
                          </Select>
                        )}
                      </td>
                      <td>
                        {user.is_active ? (
                          <Pill tone="ok" showDot>
                            активен
                          </Pill>
                        ) : (
                          <Pill tone="danger" showDot>
                            отключён
                          </Pill>
                        )}
                      </td>
                      <td className={tableCell.mono}>{fmtDate(user.created_at)}</td>
                      <td className={tableCell.mono}>{fmtDateTime(user.last_login_at)}</td>
                      <td className={tableCell.actions}>
                        <TableActions>
                          <Button size="xs" onClick={() => setPending({ kind: 'reset', user })}>
                            Сбросить пароль
                          </Button>
                          {!isMe && (
                            <Button
                              size="xs"
                              disabled={patchM.isPending}
                              onClick={() =>
                                patchM.mutate({
                                  userId: user.id,
                                  body: { is_active: !user.is_active },
                                })
                              }
                            >
                              {user.is_active ? 'Отключить' : 'Включить'}
                            </Button>
                          )}
                        </TableActions>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title="Сбросить пароль?"
        body={
          <>
            Старый пароль <code>{pending?.user.username}</code> перестанет работать сразу, и все его
            сессии закроются. Новый пароль покажем один раз, сразу после сброса.
          </>
        }
        confirmLabel="Сбросить"
        busy={resetM.isPending}
        onConfirm={confirmPending}
        onCancel={() => setPending(null)}
      />

      <CopyToast message={toast.message} />
    </div>
  );
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${fmtDate(iso)} ${iso.slice(11, 16)}`;
}
